/**
 * AutomationToggle — per-form automation on/off switch with status pill
 */

import React from 'react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useTimezone } from '@/contexts/TimezoneContext';
import type { TrackedForm } from '@/services/automationService';

interface AutomationToggleProps {
  trackedForm: TrackedForm | undefined;
  onToggle: (args: { trackedFormId: string; enabled: boolean }) => void;
  isToggling?: boolean;
}

const statusConfig = {
  success: { label: 'Success', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  fail: { label: 'Failed', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
} as const;

export const AutomationToggle: React.FC<AutomationToggleProps> = ({
  trackedForm,
  onToggle,
  isToggling,
}) => {
  if (!trackedForm) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <Switch
        checked={trackedForm.automation_enabled}
        onCheckedChange={(checked) => onToggle({ trackedFormId: trackedForm.id, enabled: checked })}
        disabled={isToggling}
        aria-label={`Toggle automation for ${trackedForm.name || trackedForm.form_id_external}`}
      />
      <span className="text-xs text-muted-foreground">
        {trackedForm.automation_enabled ? 'On' : 'Paused'}
      </span>
    </div>
  );
};

export const BackupStatusPill: React.FC<{ state: TrackedForm['last_backup_state']; timestamp?: string | null }> = ({
  state,
  timestamp,
}) => {
  const { timezone } = useTimezone();

  if (!state) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const config = statusConfig[state];

  return (
    <div className="flex flex-col gap-0.5">
      <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit', config.className)}>
        {config.label}
      </span>
      {timestamp && (
        <span className="text-xs text-muted-foreground">
          {formatRelativeTime(timestamp, timezone)}
        </span>
      )}
    </div>
  );
};

export const NextRunDisplay: React.FC<{ nextRunAt: string | null; automationEnabled: boolean }> = ({
  nextRunAt,
  automationEnabled,
}) => {
  const { timezone } = useTimezone();

  if (!automationEnabled) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (!nextRunAt) {
    return <span className="text-xs text-muted-foreground">Scheduling...</span>;
  }

  return (
    <span className="text-xs text-muted-foreground">
      {formatNextRun(nextRunAt, timezone)}
    </span>
  );
};

function formatNextRun(isoDate: string, timeZone: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffDays = getCalendarDayDiff(date, now, timeZone);

  // Past or today = due soon (planner will pick it up)
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return `In ${diffDays}d`;
}

function formatRelativeTime(isoDate: string, timeZone: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  const diffDays = getCalendarDayDiff(date, now, timeZone);

  if (diffMs < 0) {
    // Past
    const absDays = Math.abs(diffDays);
    const absHours = Math.abs(diffHours);
    if (absDays === 0 && absHours === 0) return 'Just now';
    if (absDays === 0) return `${absHours}h ago`;
    if (absDays === 1) return 'Yesterday';
    return `${absDays}d ago`;
  }

  // Future
  if (diffDays === 0 && diffHours <= 1) return 'Soon';
  if (diffDays === 0) return `In ${diffHours}h`;
  if (diffDays === 1) return 'Tomorrow ~2 AM';
  return `In ${diffDays}d`;
}

function getCalendarDayDiff(targetDate: Date, baseDate: Date, timeZone: string): number {
  const targetDay = getCalendarDayStartUtc(targetDate, timeZone);
  const baseDay = getCalendarDayStartUtc(baseDate, timeZone);
  return Math.round((targetDay - baseDay) / (1000 * 60 * 60 * 24));
}

function getCalendarDayStartUtc(date: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const year = Number(parts.find((part) => part.type === 'year')?.value || 0);
  const month = Number(parts.find((part) => part.type === 'month')?.value || 1);
  const day = Number(parts.find((part) => part.type === 'day')?.value || 1);

  return Date.UTC(year, month - 1, day);
}

export default AutomationToggle;
